import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import './tariff.scss';
import { getTariffData } from '../reducer';
import {
  deleteTariff, loadLostTicketList, loadTariffById, loadTariffByIdClear, loadTariffList,
} from '../actions';
import TextInput from '../../../components/fields/TextInput/TextInput';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  responseLogWithNotification,
  getPaginationOpts,
  getJsonFromStorage,
  useUpdatePeriod,
} from '../../../utils/utils';
import Loader from '../../../components/base/Loader/Loader';
import { getSystemData } from '../../System/reducer';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { addTariffColumns, tariffColumns } from '../constants';
import Pagination from '../../../components/base/Pagination/Pagination';
import { showGlobalConfirm } from '../../System/actions';
import notificationDecorator from '../../Notifications/notificationDecorator';
import SeparateBox from '../../../components/base/SeparateBox/SeparateBox';
import IconBox from '../../../components/common/IconBox';
import TariffForm from './TariffForm';
import TariffDTO from '../../../dto/TariffDTO';
import LostTicketPage from './LostTicketPage';

const LOST_TICKET_ROW_ID = 'lost-ticket-setting';

const isLostTicketRow = item => item?.type === 'lost_ticket';

const buildLostTicketRow = lostTicket => ({
  id: lostTicket?.id || LOST_TICKET_ROW_ID,
  __lostTicketConfigured: Boolean(lostTicket?.id),
  type: 'lost_ticket',
  type_label: 'Штрафной билет',
  name: lostTicket?.name || 'Штрафной билет',
  description: lostTicket?.description || '',
  cost: lostTicket?.cost ?? null,
  is_active: lostTicket?.id ? lostTicket.is_active : null,
  start_time: '',
  end_time: '',
  pricing_mode: '',
  vehicle_type: '',
  time_unit: '',
  free_duration: '',
  day_types: [],
  created_at: lostTicket?.created_at || null,
  updated_at: lostTicket?.updated_at || null,
});

export default function TariffListPage() {
  const dispatch = useDispatch();
  const [isCreate, setCreate] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showLostTicketForm, setShowLostTicketForm] = useState(false);
  const {
    tariffList,
    tariffListMeta,
    lostTicketList,
    loadingTariffList,
    loadingLostTicketList,
    currentTariff,
    loadingCurrentTariff,
    loadingCreateTariff,
    loadingEditTariff,
    loadingDeleteTariff,
    loadingCreateLostTicket,
    loadingEditLostTicket,
    loadingDeleteLostTicket,
    loadingIntervalList,
    loadingCreateInterval,
    loadingEditInterval,
    loadingDeleteInterval,
  } = useSelector(getTariffData);

  const { updatePeriod } = useSelector(getSystemData);

  const initial = getJsonFromStorage('tariff-filter', {});
  const { getValues, watch, control } = useForm({ defaultValues: { ...initial } });

  const loadData = opts => {
    const filterData = getValues();
    const paginationOpts = getPaginationOpts(tariffListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: filterData,
    };
    const p1 = dispatch(loadTariffList(requestData)).catch(responseLogWithNotification);
    const p2 = dispatch(loadLostTicketList({ all: true })).catch(responseLogWithNotification);
    return Promise.all([p1, p2]);
  };
  const [setPeriod, destroy] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => {
    setPeriod(updatePeriod);
    return destroy;
  }, [updatePeriod]);

  useEffect(() => {
    const tariff = watch(values => { loadData(); });
    return () => tariff.unsubscribe();
  }, [watch]);

  const handleCreate = () => {
    setShowLostTicketForm(false);
    setCreate(true);
    setShowForm(true);
  };

  const handleView = item => e => {
    if (isLostTicketRow(item)) {
      setShowLostTicketForm(true);
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }

    handleClose();
    dispatch(loadTariffById(item.id))
      .then(() => {
        setShowView(true);
      })
      .catch(responseLogWithNotification);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEdit = item => e => {
    if (isLostTicketRow(item)) {
      handleClose();
      setShowLostTicketForm(true);
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }

    handleClose();
    dispatch(loadTariffById(item.id))
      .then(() => {
        setShowForm(true);
      })
      .catch(responseLogWithNotification);
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleActive = item => e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDelete = item => e => {
    dispatch(showGlobalConfirm({
      confirmTitle: 'Удаление тарифа',
      confirmMessage: `Вы уверены, что хотите удалить тариф ${item.id}?`,
      confirmButtonText: 'Удалить',
    })).then(() => {
      dispatch(deleteTariff(item.id))
        .then(() => {
          loadData();
          handleClose();
          notificationDecorator({
            title: 'Удаление тарифа',
            message: 'Успешно',
          });
        })
        .catch(responseLogWithNotification);
    }).catch(() => {});
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClose = () => {
    dispatch(loadTariffByIdClear());
    setShowForm(false);
    setShowView(false);
    setCreate(false);
    setShowLostTicketForm(false);
  };

  const tariffDTO = new TariffDTO();
  const attributes = tariffDTO.getAttributesByName();

  const renderList = () => {
    const lostTicket = Array.isArray(lostTicketList) && lostTicketList.length ? lostTicketList[0] : null;
    const hasLostTicket = Boolean(lostTicket?.id);
    const showLostTicketRow = hasLostTicket && Number(tariffListMeta?.page || 1) === 1;
    const displayedTariffList = showLostTicketRow ? [buildLostTicketRow(lostTicket), ...tariffList] : tariffList;
    const displayedTariffMeta = {
      ...tariffListMeta,
      total: (Number(tariffListMeta?.total) || 0) + (hasLostTicket ? 1 : 0),
    };

    return (
      <div className="page-container">
        <div className="inputs-box">
          <PageHeader header="Тарифы" />
          <div className="inputs">
            {/* <FieldDTO */}
            {/*  type="text" */}
            {/*  component={TextInput} */}
            {/*  control={control} */}
            {/*  dtoObject={attributes.name} */}
            {/*  className="small no-border" */}
            {/* /> */}
            {/* <SeparateBox /> */}
            <IconBox icon="plus-add" onClick={handleCreate} />
          </div>
        </div>
        <div className="plp-table g-0">
          <div className="frame">
            <div className="sticky-table-wrapper custom-scroll-theme">
              <CustomizedGrid
                className="plp-grid"
                data={displayedTariffList}
                columns={tariffColumns}
                additionalColumns={addTariffColumns}
                name="tariff-grid"
                respectHidden
                onDoubleClickByRow={(item, event) => handleView(item)(event)}
              />
            </div>
          </div>
          <Pagination meta={displayedTariffMeta} list={displayedTariffList} handleChangeMeta={loadData} />
        </div>
      </div>
    );
  };

  const isForm = showForm && (currentTariff || isCreate);
  const isView = showView && currentTariff;

  return (
    <div className="tariff-tab">
      <Loader global loading={loadingTariffList || loadingLostTicketList || loadingCurrentTariff || loadingCreateTariff || loadingEditTariff || loadingDeleteTariff || loadingCreateLostTicket || loadingEditLostTicket || loadingDeleteLostTicket || loadingIntervalList || loadingCreateInterval || loadingEditInterval || loadingDeleteInterval} />
      {!isForm && !isView && !showLostTicketForm ? renderList() : null}
      {!isForm && isView ? <TariffForm hasView handleEdit={handleEdit} handleDelete={handleDelete} handleClose={handleClose} /> : null}
      {isForm && !isView ? <TariffForm loadData={loadData} handleEdit={handleEdit} handleDelete={handleDelete} handleClose={handleClose} /> : null}
      {showLostTicketForm ? <LostTicketPage handleClose={handleClose} closeOnSave onSaved={loadData} /> : null}
    </div>
  );
}
